import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/ui/modal";
import Button from "@/components/ui/button";
import FileInput from "@/components/ui/file-input";
import Input from "@/components/ui/input";
import { Plus as PlusIcon } from "@/components/icons";
import LibraryShareCustomFields from "../LibraryShareCustomFields";
import "./index.scss";

export type ShareOption = "everyone" | "admins" | "custom";

export interface AddDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (payload: {
    title?: string;
    share: ShareOption;
    /** Seçildiği anda yüklendiyse dolu gelir; yoksa files kullanılır */
    uploadedFileIds?: string[];
    files?: File[];
    sharedUserIds?: string[];
    sharedDepartmentIds?: string[];
  }) => void | Promise<void>;
}

const DOCUMENT_SUPPORTED_FORMATS = ["PDF", "DOC", "DOCX", "XLS", "XLSX"];
const VIDEO_SUPPORTED_FORMATS = ["MP4", "WEBM", "MOV", "M4V", "AVI"];
const MAX_SIZE_MB = 500;

const AddDocumentModal: React.FC<AddDocumentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [share, setShare] = useState<ShareOption>("everyone");
  const [title, setTitle] = useState("");
  const [contentType, setContentType] = useState<"document" | "video">("document");
  const [sharedUserIds, setSharedUserIds] = useState<string[]>([]);
  const [sharedDepartmentIds, setSharedDepartmentIds] = useState<string[]>([]);
  /** Seçildiği anda backend'e yüklenen dosya id'leri */
  const [uploadedFileIds, setUploadedFileIds] = useState<string[]>([]);

  const resetForm = () => {
    setShare("everyone");
    setTitle("");
    setContentType("document");
    setSharedUserIds([]);
    setSharedDepartmentIds([]);
    setUploadedFileIds([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFileRemove = (index: number) => {
    setUploadedFileIds((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (uploadedFileIds.length === 0) return;
    try {
      await onSubmit?.({
        share,
        title: title.trim() || undefined,
        uploadedFileIds,
        sharedUserIds: share === "custom" && sharedUserIds.length > 0 ? sharedUserIds : undefined,
        sharedDepartmentIds: share === "custom" && sharedDepartmentIds.length > 0 ? sharedDepartmentIds : undefined,
      });
      handleClose();
    } catch {
      // Hata toast'ı sayfada veya apiClient'da gösteriliyor; modal açık kalır
    }
  };

  const isFormValid = uploadedFileIds.length > 0;

  const customHeader = (
    <div className="add-document-modal-header">
      <div className="add-document-modal-header-left">
        <span className="add-document-modal-header-icon">
          <PlusIcon width={20} height={20} />
        </span>
        <h3>{t("library.addDocumentModal.title")}</h3>
      </div>
      <button
        type="button"
        className="add-document-modal-close"
        onClick={handleClose}
        aria-label={t("common.close")}
      >
        ✕
      </button>
    </div>
  );

  const customFooter = (
    <div className="add-document-modal-footer">
      <Button variant="text" color="secondary" onClick={handleClose}>
        {t("library.addDocumentModal.cancel")}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={!isFormValid}
      >
        {t("library.addDocumentModal.add")}
      </Button>
    </div>
  );

  return (
    <Modal
      title={t("library.addDocumentModal.title")}
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      customHeader={customHeader}
      customFooter={customFooter}
    >
      <div className="add-document-modal-content">
        <div className="add-document-modal-field">
          <FileInput
            label={contentType === "video" ? "Video" : undefined}
            placeholder={t("library.addDocumentModal.dropzoneText")}
            buttonLabel={t("library.addDocumentModal.addButton")}
            supportedFormats={
              contentType === "video" ? VIDEO_SUPPORTED_FORMATS : DOCUMENT_SUPPORTED_FORMATS
            }
            maxSize={MAX_SIZE_MB}
            maxFiles={1}
            onFileRemove={handleFileRemove}
            uploadToBackend
            onUploadSuccess={(fileId) => setUploadedFileIds((prev) => [...prev, fileId])}
            enableDemoProgress={false}
            fileType="file"
          />
        </div>

        <div className="add-document-modal-field">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Başlık"
            label="Başlık"
          />
          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <label>
              <input
                type="radio"
                checked={contentType === "document"}
                onChange={() => setContentType("document")}
              />{" "}
              Belge
            </label>
            <label>
              <input
                type="radio"
                checked={contentType === "video"}
                onChange={() => setContentType("video")}
              />{" "}
              Video
            </label>
          </div>
        </div>

        <div className="add-document-modal-share">
          <span className="add-document-modal-share-title">
            {t("library.addDocumentModal.share")}
          </span>
          <div className="add-document-modal-share-options">
            <label className={`add-document-modal-radio-option ${share === "everyone" ? "selected" : ""}`}>
              <input
                type="radio"
                name="document-share"
                checked={share === "everyone"}
                onChange={() => setShare("everyone")}
              />
              <span className="add-document-modal-radio-text">
                <span className="add-document-modal-radio-label">
                  {t("library.addDocumentModal.everyone")}
                </span>
                <span className="add-document-modal-radio-desc">
                  {t("library.addDocumentModal.everyoneDesc")}
                </span>
              </span>
            </label>
            <label className={`add-document-modal-radio-option ${share === "admins" ? "selected" : ""}`}>
              <input
                type="radio"
                name="document-share"
                checked={share === "admins"}
                onChange={() => setShare("admins")}
              />
              <span className="add-document-modal-radio-text">
                <span className="add-document-modal-radio-label">
                  {t("library.addDocumentModal.admins")}
                </span>
                <span className="add-document-modal-radio-desc">
                  {t("library.addDocumentModal.adminsDesc")}
                </span>
              </span>
            </label>
            <label className={`add-document-modal-radio-option ${share === "custom" ? "selected" : ""}`}>
              <input
                type="radio"
                name="document-share"
                checked={share === "custom"}
                onChange={() => setShare("custom")}
              />
              <span className="add-document-modal-radio-text">
                <span className="add-document-modal-radio-label">
                  {t("library.addDocumentModal.custom")}
                </span>
                <span className="add-document-modal-radio-desc">
                  {t("library.addDocumentModal.customDesc")}
                </span>
              </span>
            </label>
          </div>
          {share === "custom" && (
            <LibraryShareCustomFields
              sharedUserIds={sharedUserIds}
              sharedDepartmentIds={sharedDepartmentIds}
              onUsersChange={setSharedUserIds}
              onDepartmentsChange={setSharedDepartmentIds}
              isVisible={isOpen}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddDocumentModal;
